/*
  File: env.ts
  Path: src/utils/env.ts
  Description: Single source of truth for which environment this build runs in
               (local / staging / production). Powers the EnvBanner ribbon.
  Author: Larry McLean + AI Team
  Version: 1.1.0
  Last Updated: 2026-08-18

  Decision order:
    1) REACT_APP_ENV — explicit label per environment (recommended, future-proof):
         local      -> REACT_APP_ENV=local        (.env.local)
         staging    -> REACT_APP_ENV=staging      (Render: aquorix-frontend-dev)
         production -> REACT_APP_ENV=production    (Render: aquorix-frontend)
    2) No label, page served from localhost -> local (whatever API it points at).
    3) No label, not localhost -> judge by API host; only the KNOWN live API is
       production, everything else is staging.
  Fail-safe: a missing/misspelled label can never make a test site look live, nor
  paint "test" on the real live site.

  Render note: CRA only inlines process.env.REACT_APP_* into the browser bundle;
  Render's built-in vars (RENDER_SERVICE_NAME, etc.) are NOT visible to client code.

  Change Log:
    - 2026-08-18 - v1.1.0 (Larry McLean + AI Team)
      - Add localhost (window.location) check ahead of API-host derivation, so a
        local build pointed at the staging API correctly reads LOCAL.
    - 2026-08-18 - v1.0.0 (Larry McLean + AI Team)
      - Initial creation: environment detection for the environment banner.
*/

const LIVE_API_BASE = "https://aquorix-api.onrender.com"; // mirrors src/utils/api.ts default

export type AqxEnv = "local" | "staging" | "production";

export function getAqxEnv(): AqxEnv {
  // 1) Explicit label wins.
  const raw = (process.env.REACT_APP_ENV || "").trim().toLowerCase();
  if (raw === "local" || raw === "development") return "local";
  if (raw === "staging" || raw === "stage") return "staging";
  if (raw === "production" || raw === "live" || raw === "prod") return "production";

  // 2) No label: if the page is served from your machine, it's local dev —
  //    regardless of which API it happens to point at.
  const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") return "local";

  // 3) No label and not localhost: judge by the API the app talks to.
  const apiBase = (process.env.REACT_APP_API_BASE_URL || LIVE_API_BASE)
    .replace(/\/+$/, "")
    .toLowerCase();
  if (apiBase === LIVE_API_BASE.toLowerCase()) return "production";
  return "staging";
}